<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useMounted } from "@vueuse/core";
import { vIntersectionObserver } from "@vueuse/components";
import useYaSlot from "@magnit/core/src/composables/useYaSlot";
import VDisclaimer from "@magnit/core/src/containers/VDisclaimer/VDisclaimer.vue";
import { useAnalyticManager } from "@magnit/analytic-events";
import VAdBlockMessage from "./VAdBlockMessage.vue";
import type {
  IYaBannerInfo,
  IYaSlotConfig,
  IYaSlotProps,
} from "./VYaSlot.types";

const props = defineProps<IYaSlotProps>();
const emits = defineEmits<{
  stub: [];
  render: [];
}>();

const { send: nativeSend } = useAnalyticManager();
const send = props.analyticsSender || nativeSend;

const isMounted = useMounted();

const yaSlotApi = useYaSlot();

const uid = ref();
const stub = ref(false);
const visible = ref(false);
const yaSlot = ref<HTMLDivElement | null>(null);
const disclaimerContent = ref<string | undefined>();

const rootClass = computed(() => ({
  ["pl-hover-base"]: props.withHover,
  ["unit-yaslot-wrapper_product"]: props.hasProduct,
}));

const rebuildDisclaimer = (): void => {
  if (!yaSlot.value) return;
  const span: HTMLElement | null = yaSlot.value.querySelector("a>div>span");
  if (span?.textContent) {
    disclaimerContent.value = span.textContent;
    span.parentElement?.remove();
  } else {
    disclaimerContent.value = props.disclaimer;
  }
};

const onStubHandler = () => {
  emits("stub");
  stub.value = true;
};

const bannerInfo = ref<null | IYaBannerInfo>(null);

const bannerId = computed(() => bannerInfo.value?.bundleParams?.bannerId || "");
const analyticsBaseInfo = computed(() => {
  const currentItemLink = bannerInfo.value?.bundleParams?.reference
    ? new URL(bannerInfo.value?.bundleParams?.reference).searchParams.get("pf")
    : "";
  return {
    bannerId: bannerId.value,
    dest: currentItemLink || "",
  };
});

const init = async () => {
  uid.value = Math.random().toString(36).substring(2, 15);
  if (yaSlotApi.isYaContextAvailable()) {
    let configs: IYaSlotConfig[] = [];

    if (!Array.isArray(props.config)) {
      configs.push(props.config);
    } else {
      configs = props.config;
    }

    for (const config of configs) {
      yaSlotApi.pushAd(
        {
          ownerId: props.ownerId,
          containerId: `${config.id}_${uid.value}`,
          params: {
            p1: config.p1,
            p2: config.p2,
            pfb: config.pfb || "",
            pfc: config.pfc || "",
            puid1: "",
            puid2: "",
          },
          onStub: onStubHandler,
          onError: onStubHandler,
          onRender: () => {
            emits("render");
            setTimeout(rebuildDisclaimer, 300);
          },
          onLoad: (res: IYaBannerInfo) => {
            bannerInfo.value = res;
          },
        },
        ["desktop", "tablet", "phone"],
        {
          tabletWidth: 830,
          phoneWidth: 480,
        },
      );
    }
  }
};

const onSlotVisibility = ([
  { isIntersecting },
]: IntersectionObserverEntry[]) => {
  if (isIntersecting && yaSlot.value && props.analyticsView) {
    visible.value = true;
  }
};

const onSlotClick = () => {
  if (yaSlot.value && props.analyticsClick) {
    send(props.analyticsClick.name, {
      ...props.analyticsClick.options,
      ...analyticsBaseInfo.value,
    });
  }
};

watch(
  () => [props.apiReady, isMounted.value],
  (next) => {
    if (next[0] && next[1]) {
      init();
    }
  },
  { immediate: true },
);
watch(
  () => [visible.value, bannerId.value],
  (next) => {
    if (next[0] && next[1] && props.analyticsView) {
      send(props.analyticsView.name, {
        ...props.analyticsView.options,
        ...analyticsBaseInfo.value,
      });
    }
  },
);
</script>

<template>
  <div
    v-if="!apiError && !stub"
    ref="yaSlot"
    v-intersection-observer="onSlotVisibility"
    class="unit-yaslot-wrapper"
    :class="rootClass"
    @click="onSlotClick"
  >
    <div
      v-if="!Array.isArray(props.config)"
      :id="`${props.config.id}_${uid}`"
      :data-ya-slot-id="props.config.id"
      class="yaslot-item"
    />
    <div
      v-for="(item, index) in props.config"
      v-else
      :id="`${item.id}_${uid}`"
      :key="index"
      class="yaslot-item"
      :class="{
        [`yaslot-item_from-${item.from}`]: Boolean(item.from),
        [`yaslot-item_to-${item.to}`]: Boolean(item.to),
      }"
    />
    <VDisclaimer :content="disclaimerContent" />
  </div>
  <a
    v-else-if="placeholderImageSrc && placeholderUrl"
    :href="placeholderUrl"
    target="_blank"
    class="unit-yaslot-wrapper_error-placeholder unit-yaslot-wrapper_product pl-hover-base"
    :style="{
      backgroundImage: `url(${placeholderImageSrc})`,
    }"
  />
  <div
    v-else-if="placeholderImageSrc && !placeholderUrl"
    class="unit-yaslot-wrapper_error-placeholder unit-yaslot-wrapper_product"
    :style="{
      backgroundImage: `url(${placeholderImageSrc})`,
    }"
  />
  <VAdBlockMessage v-else-if="apiError" :message="props.adBlockMessage" />
</template>

<style lang="postcss" scoped>
.unit-yaslot-wrapper {
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  position: relative;

  &_product {
    overflow: hidden;
    border-radius: 12px;
  }

  &_error-placeholder {
    display: block;
    height: 100%;
    width: 100%;
    background-position: center center;
    background-size: cover;
    background-repeat: no-repeat;
    position: relative;
  }
}

.yaslot-item {
  width: 100%;
  display: none;

  :deep(img) {
    display: block;
    max-width: 100% !important;
  }

  :deep(a) {
    width: 100% !important;
    background-size: auto !important;

    & > div:has(> span) {
      /* hide adfox default disclaimer */
      display: none !important;
    }
  }

  &:empty {
    background-color: var(--pl-surface-subdued-default);
  }
}

.yaslot-item:not([class*="yaslot-item_to-"], [class*="yaslot-item_from-"]),
[class*="yaslot-item_to-"]:not([class*="yaslot-item_from-"]) {
  display: block;
}

@media (--pl-viewport-from-s) {
  .yaslot-item {
    &_from-s {
      display: block;
    }

    &_to-s {
      display: none !important;
    }
  }
}

@media (--pl-viewport-from-m) {
  .yaslot-item {
    &_from-m {
      display: block;
    }

    &_to-m {
      display: none !important;
    }
  }
}

@media (--pl-viewport-from-ml) {
  .yaslot-item {
    &_from-ml {
      display: block;
    }

    &_to-ml {
      display: none !important;
    }
  }
}

@media (--pl-viewport-from-l) {
  .yaslot-item {
    &_from-l {
      display: block;
    }

    &_to-l {
      display: none !important;
    }
  }
}

@media (--pl-viewport-from-xl) {
  .unit-yaslot-wrapper {
    &_product {
      border-radius: 16px;
    }
  }

  .yaslot-item {
    &_from-xl {
      display: block;
    }

    &_to-xl {
      display: none !important;
    }
  }
}
</style>
